import { AdminSalesEndpoints } from './adminSales/salesAdminApi';
import { BusinessListType } from './businessEntity';
import { BusinessEntityEndpoints } from './businessEntity/businessEntityApi';
import { IDeactivateContactRequest } from './contact';
import {
  IDealerOrderApiRequest,
  IDealerShipmentsRequest,
  IPreseasonApiRequest,
  IPreseasonGoalUpdateRequest,
  IPreseasonTargetUpdateRequest
} from './dealer';
import { IBlobReference, SasTokenRequestType } from './files';
import {
  ISalesCallFilters,
  ISalesComplianceFilters,
  ISalesDashboardFilters,
  ISalesProspectsFilters,
  ISalesShipmentFilters
} from './filters';
import { LookupTypes, OnDemandLookupTypes } from './lookups';
import { IFetchNewDealerRequest } from './newDealer';
import { ChangeProspectDirection } from './prospects';

export const ApiUrls = {
  activateContact: ({ businessEntityId, contactId }: IDeactivateContactRequest) =>
    `/BusinessEntities/${businessEntityId}/Contacts/${contactId}/Activate`,
  activateprospect: (prospectId: number) => `/Prospects/${prospectId}/Activate`,
  adminSales: (endpoint: AdminSalesEndpoints, id?: number) => `/Admin/Sales/${endpoint}${id ? `/${id}` : ''}`,
  adminSalesDistrictGoals: (fiscalYear?: string, salesDistricts?: string[]) =>
    `/Admin/Sales/SalesDistrictGoals${fiscalYear ? `?fiscalYear=${fiscalYear}` : ''}${
      salesDistricts ? createQueryString('salesDistricts', salesDistricts) : ''
    }`,
  axAddressUpdate: () => '/Addresses/UpdateFromAx',
  azureStorageSas: (type: SasTokenRequestType) => `/AzureStorageSas?type=${type}`,
  bigCommerceShipMethod: (shipMethodId?: number) => `/BigCommerce/ShipMethods${shipMethodId ? `/${shipMethodId}` : ''}`,
  bigCommerceStore: (storeId?: number) => `/BigCommerce/Stores${storeId ? `/${storeId}` : ''}`,
  businessEntity: (beId: number, endPoint?: BusinessEntityEndpoints) =>
    `/BusinessEntities/${beId}${endPoint ? `/${endPoint}` : ''}`,
  businessEntitySearch: (req: { type: BusinessListType; zipcode: string }) =>
    `/BusinessEntities/Search?zipcode=${req.zipcode}&type=${req.type}`,
  carriers: (carrierId?: string, type: { loads?: boolean } = {}) =>
    `/Logistics/Carriers${carrierId ? `/${carrierId}` : ''}${type.loads ? '/Loads' : ''}`,
  cartLines: (cartId: number) => `/Carts/${cartId}/CartLines`,
  carts: (cartId?: number) => `/Carts${cartId ? `/${cartId}` : ''}`,
  cashDiscountQuery: (values: { accountNum: string; fiscalYear: string }) =>
    `/Programs/CashDiscounts/Query?accountNum=${values.accountNum}&fiscalYear=${values.fiscalYear}`,
  changeProspectStep: (prospectId: number, direction: ChangeProspectDirection) =>
    `/Prospects/${prospectId}/${direction === 'advance' ? 'AdvanceStep' : 'RevertStep'}`,
  config: (section?: string) => `/Config${section ? `?section=${section}` : ''}`,
  contactsByBusinessEntity: (businessEntityId: number) => `/BusinessEntities/${businessEntityId}/Contacts`,
  contactsById: (contactIds: number[]) => `/Contacts?${createQueryString('contactIds', contactIds)}`,
  coOpCampaignTypes: (campaignTypeId?: number) =>
    `/CoOp/CampaignTypes${campaignTypeId ? `?campaignTypeId=${campaignTypeId}` : ''}`,
  coOpDealerEstiamte: (businessEntityId?: number, fiscalYear?: string) =>
    `/CoOp/DealerEstimates?${businessEntityId ? `&businessEntityId=${businessEntityId}` : ''}${
      fiscalYear ? `&fiscalYear=${fiscalYear}` : ''
    }`,
  coOpDealerRequests: (accountNum: string) => `/CoOp/Dealer?accountNum=${accountNum}`,
  coOpGlobalSettings: (fiscalYear?: string) => `/CoOp/GlobalSettings${fiscalYear ? `?fiscalYear=${fiscalYear}` : ''}`,
  coOpRegionBudgets: (fiscalYear?: string) => `/CoOp/RegionBudgets${fiscalYear ? `?fiscalYear=${fiscalYear}` : ''}`,
  coOpRequest: (coOpRequestId: number) => `/CoOp/${coOpRequestId}`,
  coOpRequests: (salesDistricts?: string[], fiscalYear?: string, finance?: boolean) =>
    `/CoOp?${salesDistricts ? `${createQueryString('salesDistricts', salesDistricts)}` : ''}${
      fiscalYear ? `&fiscalYear=${fiscalYear}` : ''
    }${`&finance=${finance}`}`,
  coOpRequestStatuses: (coOpRequestId: number) => `/CoOp/${coOpRequestId}/Statuses`,
  coOpSalesDistrictBudgets: (salesDistricts?: string[], fiscalYear?: string) =>
    `/CoOp/SalesDistrictBudgets${fiscalYear ? `?fiscalYear=${fiscalYear}` : ''}${
      salesDistricts ? createQueryString('salesDistricts', salesDistricts, true) : ''
    }`,
  coOpStates: (coOpStateId?: number) => `/CoOp/States${coOpStateId ? `?coOpStateId=${coOpStateId}` : ''}`,
  coOpStatus: () => `/CoOp/Status`,
  deactivateContact: ({ businessEntityId, contactId }: IDeactivateContactRequest) =>
    `/BusinessEntities/${businessEntityId}/Contacts/${contactId}/Deactivate`,
  deactivateProspect: (prospectId: number) => `/Prospects/${prospectId}/Deactivate`,
  dealerPowerAssessments: (accountNum: string) => `/Dealers/${accountNum}/PowerAssessments`,
  departingSoon: ({ date, loadNumber, shipmentId }: { date?: string; loadNumber?: string; shipmentId?: number }) =>
    `/Operations/DepartingSoon?${date ? `date=${date}` : ''}${loadNumber ? `loadNumber=${loadNumber}` : ''}${
      shipmentId ? `shipmentId=${shipmentId}` : ''
    }`,
  displayConfigs: (userSid?: string) => `/Displays/Configs${userSid ? `/${userSid}` : ''}`,
  dpNewsFeedPostAccess: (dpNewsFeedPostAccessId: number) => `/DpNewsFeed/Posts/Access/${dpNewsFeedPostAccessId}`,
  dpNewsFeedPostCheckAccess: (postId: number) => `/DpNewsFeed/Posts/${postId}/CheckAccess`,
  dpNewsFeedPosts: (postId?: number) => `/DpNewsFeed/Posts${postId ? `/${postId}` : ''}`,
  dpNewsFeedTag: () => `/DpNewsFeed/Tags`,
  employeeSearch: (searchTerm: string, type: 'user' | 'global' = 'user') =>
    `/Search/Employee?searchTerm=${searchTerm}&type=${type}`,
  files: (ref?: IBlobReference) => `/Files${ref ? `?container=${ref.container}&folder=${ref.folder}` : ''}`,
  freezeProspect: (prospectId: number) => `/Prospects/${prospectId}/Freeze`,
  getBusinessEntitySalesCalls: (businessEntityId: number, skip: number = 0) =>
    `/BusinessEntities/${businessEntityId}/SalesCalls?skip=${skip}`,
  getCounties: (stateFipsId: string) => `/Lookups?stateFipsId=${stateFipsId}`,
  getDealer: (accountNum: string) => `/Dealers/${accountNum}`,
  getDealerFinancials: (accountNum: string) => `/Dealers/${accountNum}/Financials`,
  getDealerOrders: (request: IDealerOrderApiRequest) => `/Dealers/${request.accountNum}/Orders?skip=${request.skip}`,
  getDealerPreseason: (request: IPreseasonApiRequest) =>
    `/Dealers/${request.accountNum}/Preseason?fiscalYear=${request.fiscalYear}&includePrograms=${request.includePrograms}`,
  getDealers: (salesDistricts: string[], searchTerm: string = '') =>
    `/Dealers?${salesDistricts.map(x => `salesDistricts=${x}`).join('&')}&searchTerm=${searchTerm}`,
  getDealerShipments: (request: IDealerShipmentsRequest) =>
    `/Dealers/${request.accountNum}/Shipments?skip=${request.skip}`,
  getLookups: (type?: LookupTypes) => `/Lookups${type ? `?lookupType=${type}` : ''}`,
  getLookupsOnDemand: (types: OnDemandLookupTypes[]) => `/Lookups/OnDemand?${createQueryString('types', types, false)}`,
  getOpenOrders: (salesDistricts: string[], companies: string[], skip: number = 0) =>
    `/Orders/Open?skip=${skip}${createQueryString('salesDistricts', salesDistricts)}${createQueryString(
      'companies',
      companies
    )}`,
  getOrder: (salesId: string) => `/Orders/${salesId}`,
  getPermissions: (sid?: string) => `/Permissions${sid ? `?sid=${sid}` : ''}`,
  getPrograms: (fiscalYear: string) => `/Programs?fiscalYear=${fiscalYear}`,
  getProspects: (salesDistricts: string[], filter: ISalesProspectsFilters, skip: number) =>
    `/Prospects?${createQueryString('salesDistricts', salesDistricts, false)}${createQueryString(
      'lifeCycleSteps',
      filter.lifeCycleSteps
    )}${createQueryString('businessTypes', filter.businessTypes)}&skip=${skip}&prospectStatusId=${
      filter.prospectStatusId
    }&searchTerm=${filter.searchTerm}&sortBy=${filter.sortBy}${createQueryString('starRating', filter.starRating)}`,
  getSalesDashboard: (filters: ISalesDashboardFilters) =>
    Object.entries(filters).reduce<string>((query, [key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          query += createQueryString(key, value, !query.endsWith('?'));
        }
      }

      return query;
    }, '/Dashboard/Sales?'),
  getShipments: (filters: ISalesShipmentFilters) =>
    `/Shipments?${createQueryString('salesDistricts', filters.salesDistricts, false)}${createQueryString(
      'loadStates',
      filters.loadStates
    )}&startDate=${new Date(filters.dateRange.start).toUTCString()}&endDate=${new Date(
      filters.dateRange.end
    ).toUTCString()}`,
  inventoryQuery: () => `/Inventory/Query`,
  link: (linkId: number) => `/Links/${linkId}`,
  links: (type?: string) => `/Links${type ? `?type=${type}` : ''}`,
  // loadCalendar: (startDate: Date, endDate: Date, warehouses: string[]) =>
  //   `/Logistics/Calendar?startDate=${encodeURIComponent(startDate.toUTCString())}&endDate=${encodeURIComponent(
  //     endDate.toUTCString()
  //   )}${createQueryString('warehouses', warehouses)}`,
  loads: ({
    type = {},
    dates,
    loadNumber,
    warehouses
  }: {
    dates?: { startDate: Date; endDate?: Date };
    warehouses?: string[];
    loadNumber?: string;
    type?: {
      advance?: boolean;
      shipments?: boolean;
      departureDate?: boolean;
      fees?: boolean;
      route?: boolean;
      events?: boolean;
    };
  } = {}) =>
    `/Logistics/Loads${
      dates
        ? `?startDate=${encodeURIComponent(dates.startDate.toUTCString())}&endDate=${
            dates.endDate ? encodeURIComponent(dates.endDate.toUTCString()) : ''
          }`
        : ''
    }${warehouses ? createQueryString('warehouses', warehouses) : ''}${loadNumber ? `/${loadNumber}` : ''}${
      type.advance ? '/Advance' : ''
    }${type.shipments ? '/Shipments' : ''}${type.departureDate ? '/UpdateDepartureDate' : ''}${
      type.fees ? '/Fees' : ''
    }${type.route ? '/Routes' : ''}${type.events ? '/Events' : ''}`,
  logisticsAdmin: (values: { config?: boolean }) => `/Admin/Logistics${values.config ? '/Config' : ''}`,
  logisticsWarehouseGroups: (warehouseGroupId?: number) =>
    `/Logistics/WarehouseGroups${warehouseGroupId ? `/${warehouseGroupId}` : ''}`,
  logisticsWarehouses: () => '/Logistics/Warehouses',
  newDealer: ({ businessEntityId = -1, newDealerRequestId = -1, prospectId = -1 }: IFetchNewDealerRequest) =>
    `/NewDealer?businessEntityId=${businessEntityId}&newDealerRequestId=${newDealerRequestId}&prospectId=${prospectId}`,
  newDealerAdvanceState: (newDealerRequestId: number) => `/NewDealer/${newDealerRequestId}/Advance`,
  newDealerCatalogSearch: (cartId: number, searchTerm: string) =>
    `/NewDealer/Catalog/Search?cartId=${cartId}&searchTerm=${searchTerm}`,
  newDealerComplete: (newDealerRequestId: number) => `/NewDealer/${newDealerRequestId}/Complete`,
  newDealerProgramLevel: () => `/NewDealer/Levels`,
  newDealerRequest: (newDealerRequestId: number) => `/NewDealer/${newDealerRequestId}`,
  newDealerRequestCart: (cartId?: number) => `/NewDealer/Carts${cartId ? `/${cartId}` : ''}`,
  newDealerRequestCompleteEvent: () => `/NewDealer/CompleteEvents`,
  newDealerRequestCreateCustomer: (newDealerRequestId: number) => `/NewDealer/${newDealerRequestId}/Create`,
  newDealerRequestEvent: (newDealerRequestEventId?: number) =>
    `/NewDealer/Events${newDealerRequestEventId ? `/${newDealerRequestEventId}` : ''}`,
  newDealerRequestImportOrders: (newDealerRequestId: number) => `/NewDealer/${newDealerRequestId}/ImportOrders`,
  newDealerRequestMinList: () => `/NewDealer/MinList`,
  newDealerRequestReject: (newDealerRequestId: number) => `/NewDealer/${newDealerRequestId}/Reject`,
  newDealerRequestValidate: (newDealerRequestId: number) => `/NewDealer/${newDealerRequestId}/Validate`,
  programCashDiscount: (val?: { fiscalYear?: string; programCashDiscountId?: number }) =>
    `/Programs/CashDiscounts${val && val.programCashDiscountId ? `/${val.programCashDiscountId}` : ''}${
      val && val.fiscalYear ? `?fiscalYear=${val.fiscalYear}` : ''
    }`,
  programGroup: (programGroupId?: number) => `/Programs/Groups${programGroupId ? `/${programGroupId}` : ''}`,
  programLevel: (programLevelIdId?: number) => `/Programs/Levels${programLevelIdId ? `/${programLevelIdId}` : ''}`,
  programPreSeasonSetup: (fiscalYear?: string) =>
    `/Programs/PreSeasonSetup${fiscalYear ? `?fiscalYear=${fiscalYear}` : ''}`,
  prospect: (prospectId?: number) => `/Prospects${prospectId ? `/${prospectId}` : ''}`,
  prospectByBusinessEntityId: (businessEntityId: number) => `/Prospects/BusinessEntity/${businessEntityId}`,
  prospectByProspectId: (prospectId: number) => `/Prospects/${prospectId}`,
  prospectsRoot: () => `/Prospects`,
  salesCall: (salesCallId: number) => `/SalesCalls/${salesCallId}`,
  salesCalls: () => `/SalesCalls`,
  salesCallsByDate: (date: Date | string, salesDistricts: string[]) =>
    `/SalesCalls?date=${date}&${salesDistricts.map(x => `salesDistricts=${x}`).join('&')}`,
  salesCallsInfo: () => `/SalesCalls/InfoCall`,
  salesCallsPaged: (filters: ISalesCallFilters, skip: number) =>
    `/SalesCalls?sortBy=${filters.sortBy}&searchTerm=${filters.searchTerm}&skip=${skip}${createQueryString(
      'salesDistricts',
      filters.salesDistricts
    )}${createQueryString('callTypes', filters.callTypes)}${createQueryString(
      'callMethods',
      filters.callMethods
    )}&sid=${filters.sid}${createQueryString('brands', filters.brands)}`,
  salesCompliance: (filters: ISalesComplianceFilters) =>
    `/Compliance/Sales?fiscalYear=${filters.fiscalYear}&sid=${filters.sid}${createQueryString(
      'regions',
      filters.regions,
      true
    )}`,
  salesDocument: () => `/Sales/Documents`,
  salesDocuments: (prefix?: string) => (prefix ? `/Sales/Documents?prefix=${prefix}` : `/Sales/Documents`),
  salesRepCallGoals: (sid?: string, fiscalYear?: string) =>
    `/Sales/Reps/CallGoals${sid ? `?sid=${sid}` : ''}${fiscalYear ? `&fiscalYear=${fiscalYear}` : ''}`,
  salesReps: (sid?: string) => `/Sales/Reps${sid ? `/${sid}` : ''}`,
  shipment: (shipmentId?: number) => `/Shipments${shipmentId ? `/${shipmentId}` : ''}`,
  shipmentItem: (shipmentItemId?: number) => `/ShipmentItems${shipmentItemId ? `/${shipmentItemId}` : ''}`,
  targetedCatalog: ({ brand, full }: { brand?: string; full?: boolean }) =>
    `/TargetedCatalog${brand ? `?brand=${brand}` : ''}${full ? `?full=${true}` : ''}`,
  targetedCatalogItem: (catalogItemId?: number) => `/TargetedCatalog/Item${catalogItemId ? `/${catalogItemId}` : ''}`,
  targetedCatalogItemGroup: ({ groupId, order }: { order?: boolean; groupId?: number }) =>
    `/TargetedCatalog/Group${groupId ? `/${groupId}` : ''}${order ? '/UpdateOrder' : ''}`,
  targetedCatalogItemSearch: (sectionId: number, searchTerm: string) =>
    `/TargetedCatalog/ItemSearch?sectionId=${sectionId}&searchTerm=${searchTerm}`,
  targetedCatalogSection: (sectionId?: number) => `/TargetedCatalog/Section${sectionId ? `/${sectionId}` : ''}`,
  unallocatedProducts: (partialUpdateId: string = '') =>
    `/Logistics/UnallocatedProducts${partialUpdateId ? `?updateId=${partialUpdateId}` : ''}`,
  updateDealerPreseasonGoal: (request: IPreseasonGoalUpdateRequest) => `/Dealers/${request.accountNum}/Preseason/Goals`,
  updateDealerPreseasonTarget: (request: IPreseasonTargetUpdateRequest) =>
    `/Dealers/${request.accountNum}/Preseason/Target`
};

function createQueryString(key: string, values: (string | number)[], includePrefix: boolean = true): string {
  if (values.length > 0) {
    return `${includePrefix ? '&' : ''}${values.map(x => `${key}=${x}`).join('&')}`;
  }
  return '';
}
